import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import MenuIcon from "@mui/icons-material/Menu";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import { Box, CircularProgress, Paper, SpeedDial, SpeedDialAction, Stack, Typography } from "@mui/material";
import { CanvasView, GRID_CONSTANTS, PageMeta } from "store/types";

import { CanvasCard } from "./CanvasCard";

const { CANVAS_WIDTH, CANVAS_HEIGHT, MARGIN, HEADER_HEIGHT, FOOTER_HEIGHT, GAP, GRID_CELL_HEIGHT } = GRID_CONSTANTS;

interface DragPreview {
  col: number;
  row: number;
  span: number;
  isValid: boolean;
}

interface CardDragPreview {
  col: number;
  row: number;
  isValid: boolean;
}

interface CanvasProps {
  canvasRef: React.RefObject<HTMLDivElement>;
  loading: boolean;
  currentPageCards: CanvasView[];
  currentPageMeta: PageMeta;
  backgroundUrl: string | null;
  logoUrl: string | null;
  reportTitle: string;
  gridCellWidth: number;
  dragPreview: DragPreview | null;
  cardDragPreview: CardDragPreview | null;
  draggingCardId: string | null;
  resizingCardId: string | null;
  hasContent: boolean;
  exporting: boolean;
  showNewReportAction?: boolean;
  onCanvasDragOver: (e: React.DragEvent) => void;
  onCanvasDrop: (e: React.DragEvent) => void;
  onCanvasDragLeave: () => void;
  onCardDragStart: (card: CanvasView, e: React.MouseEvent) => void;
  onCardDragMove: (e: React.MouseEvent) => void;
  onCardDragEnd: () => void;
  onResizeStart: (card: CanvasView, e: React.MouseEvent) => void;
  onResizeMove: (e: React.MouseEvent) => void;
  onResizeEnd: () => void;
  onOpenCustomization: (card: CanvasView) => void;
  onRemoveCard: (cardId: string) => void;
  onNewReport?: () => void;
  onAddPage?: () => void;
  onRemovePage?: () => void;
  canRemovePage?: boolean;
  onExportPdf?: () => void;
  getCardWidth: (span: number) => number;
  getCardHeight: (rowSpan: number) => number;
  renderCardContent: (card: CanvasView) => React.ReactNode;
}

export const Canvas: React.FC<CanvasProps> = ({
  canvasRef,
  loading,
  currentPageCards,
  currentPageMeta,
  backgroundUrl,
  logoUrl,
  reportTitle,
  gridCellWidth,
  dragPreview,
  cardDragPreview,
  draggingCardId,
  resizingCardId,
  hasContent,
  exporting,
  showNewReportAction,
  onCanvasDragOver,
  onCanvasDrop,
  onCanvasDragLeave,
  onCardDragStart,
  onCardDragMove,
  onCardDragEnd,
  onResizeStart,
  onResizeMove,
  onResizeEnd,
  onOpenCustomization,
  onRemoveCard,
  onNewReport,
  onAddPage,
  onRemovePage,
  canRemovePage,
  onExportPdf,
  getCardWidth,
  getCardHeight,
  renderCardContent
}) => {
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const isResizing = resizingCardId !== null;
  const isDraggingCard = draggingCardId !== null;

  return (
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        bgcolor: "#f6f8fc",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: "20px",
        position: "relative"
      }}
    >
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          ref={canvasRef}
          elevation={3}
          onDragOver={onCanvasDragOver}
          onDrop={onCanvasDrop}
          onDragLeave={onCanvasDragLeave}
          sx={{
            width: CANVAS_WIDTH,
            height: CANVAS_HEIGHT,
            position: "relative",
            bgcolor: "white",
            overflow: "hidden",
            borderRadius: "8px",
            boxShadow: "0 1px 2px 0 #1212171a"
          }}
        >
          {exporting && (
            <Box
              data-export-indicator
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "rgba(255, 255, 255, 0.85)",
                zIndex: 200,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2
              }}
            >
              <CircularProgress size={40} sx={{ color: "#0B4549" }} />
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#1e293b" }}>Exporting PDF...</Typography>
            </Box>
          )}
          {/* Background image layer */}
          {backgroundUrl && (
            <Box
              data-export-background
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${backgroundUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                opacity: (currentPageMeta.backgroundOpacity ?? 100) / 100,
                zIndex: 0,
                pointerEvents: "none"
              }}
            />
          )}
          <Stack
            data-export-header
            direction="row"
            justifyContent={logoUrl ? "flex-start" : "center"}
            alignItems="center"
            gap={2}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: HEADER_HEIGHT,
              borderBottom: "1px dashed #e0e0e0",
              px: logoUrl ? 3 : 0
            }}
          >
            {logoUrl && (
              <Box
                data-export-logo
                component="img"
                src={logoUrl}
                alt="Report Logo"
                sx={{
                  height: 50,
                  maxWidth: 120,
                  objectFit: "contain"
                }}
              />
            )}
            <Typography variant="h6" color="#071F35">
              {reportTitle || "Report Title"}
            </Typography>
          </Stack>
          {/* Grid area */}
          <Box
            data-export-grid
            onMouseMove={isResizing ? onResizeMove : isDraggingCard ? onCardDragMove : undefined}
            onMouseUp={isResizing ? onResizeEnd : isDraggingCard ? onCardDragEnd : undefined}
            onMouseLeave={isResizing ? onResizeEnd : isDraggingCard ? onCardDragEnd : undefined}
            sx={{
              position: "absolute",
              top: MARGIN + HEADER_HEIGHT,
              left: MARGIN,
              right: MARGIN,
              bottom: MARGIN + FOOTER_HEIGHT,
              backgroundImage: `
                linear-gradient(to right, #f0f0f0 1px, transparent 1px),
                linear-gradient(to bottom, #f0f0f0 1px, transparent 1px)
              `,
              backgroundSize: `50.1px ${GRID_CELL_HEIGHT + GAP}px`
            }}
          >
            {/* Drag preview for new cards */}
            {dragPreview && (
              <Box
                sx={{
                  position: "absolute",
                  left: dragPreview.col * (gridCellWidth + GAP),
                  top: dragPreview.row * (GRID_CELL_HEIGHT + GAP),
                  width: getCardWidth(dragPreview.span),
                  height: GRID_CELL_HEIGHT * 2 + GAP,
                  bgcolor: dragPreview.isValid ? "rgba(37, 99, 235, 0.2)" : "rgba(239, 68, 68, 0.2)",
                  border: `2px dashed ${dragPreview.isValid ? "#2563eb" : "#ef4444"}`,
                  borderRadius: 1,
                  pointerEvents: "none",
                  zIndex: 100
                }}
              />
            )}
            {cardDragPreview && draggingCardId && (
              <Box
                sx={{
                  position: "absolute",
                  left: cardDragPreview.col * (gridCellWidth + GAP),
                  top: cardDragPreview.row * (GRID_CELL_HEIGHT + GAP),
                  width: getCardWidth(currentPageCards.find((c) => c.id === draggingCardId)?.gridSpan || 6),
                  height: getCardHeight(currentPageCards.find((c) => c.id === draggingCardId)?.gridRowSpan || 2),
                  bgcolor: cardDragPreview.isValid ? "rgba(37, 99, 235, 0.2)" : "rgba(239, 68, 68, 0.2)",
                  border: `2px dashed ${cardDragPreview.isValid ? "#2563eb" : "#ef4444"}`,
                  borderRadius: 1,
                  pointerEvents: "none",
                  zIndex: 50
                }}
              />
            )}
            {currentPageCards.map((card) => (
              <CanvasCard
                key={card.id}
                card={card}
                gridCellWidth={gridCellWidth}
                isDragging={draggingCardId === card.id}
                onDragStart={onCardDragStart}
                onResizeStart={onResizeStart}
                onOpenCustomization={onOpenCustomization}
                onRemove={onRemoveCard}
                getCardWidth={getCardWidth}
                getCardHeight={getCardHeight}
              >
                {renderCardContent(card)}
              </CanvasCard>
            ))}
            {!currentPageCards.length && !dragPreview && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center"
                }}
              >
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  Drag elements here to build your report
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  or click on an element from the left panel
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}
      <SpeedDial
        ariaLabel="Report actions"
        open={speedDialOpen}
        onOpen={() => setSpeedDialOpen(true)}
        onClose={() => setSpeedDialOpen(false)}
        icon={<MenuIcon />}
        direction="down"
        sx={{
          position: "sticky",
          top: 16,
          alignSelf: "flex-start",
          zIndex: 10,
          "& .MuiFab-primary": {
            bgcolor: "#0B4549",
            "&:hover": {
              bgcolor: "#083538"
            }
          }
        }}
      >
        {showNewReportAction && onNewReport && (
          <SpeedDialAction
            icon={<NoteAddIcon />}
            tooltipTitle="New Report"
            tooltipPlacement="right"
            onClick={() => {
              onNewReport();
              setSpeedDialOpen(false);
            }}
          />
        )}
        {onAddPage && (
          <SpeedDialAction
            icon={<AddIcon />}
            tooltipTitle="Add Page"
            tooltipPlacement="right"
            onClick={() => {
              onAddPage();
              setSpeedDialOpen(false);
            }}
          />
        )}
        {onRemovePage && (
          <SpeedDialAction
            icon={<DeleteIcon />}
            tooltipTitle="Remove Page"
            tooltipPlacement="right"
            FabProps={{ disabled: !canRemovePage }}
            onClick={() => {
              if (canRemovePage) {
                onRemovePage();
                setSpeedDialOpen(false);
              }
            }}
          />
        )}
        {onExportPdf && (
          <SpeedDialAction
            icon={<DownloadIcon />}
            tooltipTitle={exporting ? "Exporting..." : "Export PDF"}
            tooltipPlacement="right"
            onClick={() => {
              if (!exporting && hasContent) {
                onExportPdf();
                setSpeedDialOpen(false);
              }
            }}
            FabProps={{
              disabled: exporting || !hasContent
            }}
          />
        )}
      </SpeedDial>
    </Box>
  );
};
